import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card, Badge, FileUpload } from "../components/ui";

const TEMPLATE_OPTIONS = [
  {
    value: "arjo",
    label: "ARJO - Rapporto Intervento",
    desc: "PDF rapporto intervento ARJO con pagine dettaglio",
  },
  {
    value: "cura",
    label: "CURA Soluzioni - Verifiche Elettromedicali",
    desc: "PDF verifiche sicurezza elettrica apparecchiature",
  },
  {
    value: "autoclave",
    label: "CURA Soluzioni - Verifica Autoclave/Laboratorio",
    desc: "PDF verifiche autoclave e apparecchiature da laboratorio (CEI 66-5)",
  },
  {
    value: "excel",
    label: "Inventario Apparecchiature (Excel)",
    desc: "File XLSX con inventario strutturato",
  },
  {
    value: "cura-ocr",
    label: "CURA Soluzioni - PDF Scansionato (OCR)",
    desc: "PDF basato su immagini - richiede OCR (tesseract)",
  },
];

const STEPS = [
  { id: 1, title: "Caricamento", desc: "Seleziona file e parametri" },
  { id: 2, title: "Anteprima", desc: "Verifica dati estratti" },
  { id: 3, title: "Import", desc: "Conferma e importa" },
];

function ImportApparecchiaturePage() {
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  const { showToast } = useToast();

  // Wizard state
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState("import"); // "import" | "update"

  // Step 1 state
  const [file, setFile] = useState(null);
  const [templateType, setTemplateType] = useState("");
  const [rsaId, setRsaId] = useState("");
  const [rsaList, setRsaList] = useState([]);
  const [parsing, setParsing] = useState(false);

  // Step 2 state
  const [equipment, setEquipment] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [parseWarnings, setParseWarnings] = useState([]);
  const [parseStats, setParseStats] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Step 3 state
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importProgress, setImportProgress] = useState(0);

  // Load RSA list
  useEffect(() => {
    apiRequest("/rsa")
      .then((data) => setRsaList(data.value || []))
      .catch(() => showToast("Errore nel caricamento delle RSA", "error"));
  }, [apiRequest, showToast]);

  // ============================================================================
  // Step 1: Parse file
  // ============================================================================

  const handleParse = useCallback(async () => {
    if (!file) return showToast("Seleziona un file", "error");
    if (!templateType) return showToast("Seleziona un template", "error");
    if (!rsaId) return showToast("Seleziona una RSA", "error");

    setParsing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("templateType", templateType);

      const result = await apiRequest("/apparecchiature/import/parse", {
        method: "POST",
        body: formData,
      });

      setEquipment(result.equipment || []);
      setParseErrors(result.errors || []);
      setParseWarnings(result.warnings || []);
      setParseStats(result.stats || null);

      // Select all rows by default
      setSelectedRows(new Set(result.equipment.map((_, i) => i)));

      if (result.equipment.length === 0) {
        showToast(
          "Nessuna apparecchiatura estratta dal file. Controlla template e formato.",
          "warning",
        );
      } else {
        setStep(2);
        showToast(
          `Estratte ${result.equipment.length} apparecchiature`,
          "success",
        );
      }
    } catch (err) {
      showToast(
        err.data?.error || "Errore durante il parsing del file",
        "error",
      );
    } finally {
      setParsing(false);
    }
  }, [file, templateType, rsaId, apiRequest, showToast]);

  // ============================================================================
  // Step 2: Edit & review
  // ============================================================================

  const toggleRow = (index) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === equipment.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(equipment.map((_, i) => i)));
    }
  };

  const removeRow = (index) => {
    setEquipment((prev) => prev.filter((_, i) => i !== index));
    setSelectedRows((prev) => {
      const next = new Set();
      for (const i of prev) {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      }
      return next;
    });
  };

  const updateField = (index, field, value) => {
    setEquipment((prev) =>
      prev.map((eq, i) => (i === index ? { ...eq, [field]: value } : eq)),
    );
  };

  // ============================================================================
  // Step 3: Execute import
  // ============================================================================

  const handleImport = useCallback(async () => {
    const selected = equipment.filter((_, i) => selectedRows.has(i));
    if (selected.length === 0) {
      return showToast("Seleziona almeno un'apparecchiatura", "error");
    }

    setImporting(true);
    setImportProgress(0);
    setStep(3);

    try {
      // Simulate progress (actual request is single POST)
      const progressInterval = setInterval(() => {
        setImportProgress((p) => Math.min(p + 2, 90));
      }, 200);

      const endpoint =
        mode === "update"
          ? "/apparecchiature/import/execute-update"
          : "/apparecchiature/import/execute";

      const body =
        mode === "update"
          ? { equipment: selected, rsaId }
          : { equipment: selected, rsaId, skipExisting: true };

      const result = await apiRequest(endpoint, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
      });

      clearInterval(progressInterval);
      setImportProgress(100);
      setImportResults(result);

      if (mode === "update") {
        if (result.updated > 0) {
          showToast(
            `Aggiornamento completato: ${result.updated} aggiornate, ${result.unmatched} non trovate`,
            "success",
          );
        } else {
          showToast(
            "Nessuna apparecchiatura trovata per aggiornamento",
            "warning",
          );
        }
      } else if (result.created > 0) {
        showToast(
          `Import completato: ${result.created} create, ${result.skipped} saltate`,
          "success",
        );
      } else if (result.skipped > 0) {
        showToast("Tutte le apparecchiature erano gia presenti", "info");
      }
    } catch (err) {
      setImportResults({
        created: 0,
        skipped: 0,
        errors: [{ error: err.data?.error || err.message }],
      });
      showToast("Errore durante l'import", "error");
    } finally {
      setImporting(false);
    }
  }, [equipment, selectedRows, rsaId, apiRequest, showToast]);

  // ============================================================================
  // Render
  // ============================================================================

  const rsaName = rsaList.find((r) => r.id === rsaId)?.nome || "";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {mode === "update"
              ? "Aggiorna Verifiche"
              : "Import Apparecchiature"}
          </h1>
          <p className="text-gray-500 mt-1">
            {mode === "update"
              ? "Aggiorna date manutenzione da rapportini di verifica"
              : "Importa apparecchiature da file PDF o Excel"}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => navigate("/apparecchiature")}
        >
          Torna alla lista
        </Button>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.id}>
            {i > 0 && (
              <div
                className={`flex-1 h-0.5 ${step > s.id - 1 ? "bg-primary-500" : "bg-gray-200"}`}
              />
            )}
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step === s.id
                    ? "bg-primary-500 text-white"
                    : step > s.id
                      ? "bg-primary-100 text-primary-700"
                      : "bg-gray-200 text-gray-500"
                }`}
              >
                {step > s.id ? "\u2713" : s.id}
              </div>
              <div className="hidden sm:block">
                <p
                  className={`text-sm font-medium ${step === s.id ? "text-primary-700" : "text-gray-500"}`}
                >
                  {s.title}
                </p>
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <Card className="p-6 space-y-6">
          {/* Mode selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modalita operazione *
            </label>
            <div className="flex gap-3">
              {[
                {
                  value: "import",
                  label: "Importa Nuove",
                  desc: "Crea nuove apparecchiature in Dataverse",
                },
                {
                  value: "update",
                  label: "Aggiorna Verifiche",
                  desc: "Aggiorna date manutenzione su apparecchiature esistenti",
                },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`flex-1 flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    mode === opt.value
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="mode"
                    value={opt.value}
                    checked={mode === opt.value}
                    onChange={(e) => setMode(e.target.value)}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-sm">{opt.label}</p>
                    <p className="text-xs text-gray-500">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Template selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo Template *
              </label>
              <div className="space-y-2">
                {TEMPLATE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      templateType === opt.value
                        ? "border-primary-500 bg-primary-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="templateType"
                      value={opt.value}
                      checked={templateType === opt.value}
                      onChange={(e) => setTemplateType(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-sm">{opt.label}</p>
                      <p className="text-xs text-gray-500">{opt.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* RSA selection + file upload */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  RSA di destinazione *
                </label>
                <select
                  value={rsaId}
                  onChange={(e) => setRsaId(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Seleziona RSA...</option>
                  {rsaList.map((rsa) => (
                    <option key={rsa.id} value={rsa.id}>
                      {rsa.nome}
                    </option>
                  ))}
                </select>
              </div>

              <FileUpload
                label="File da importare *"
                accept=".pdf,.xlsx,.xls"
                maxSize={50}
                onChange={setFile}
                value={file}
                helper="Formati: PDF (ARJO, CURA), XLSX (Inventario). Max 50MB."
              />
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button
              onClick={handleParse}
              disabled={!file || !templateType || !rsaId || parsing}
            >
              {parsing ? (
                <span className="flex items-center gap-2">
                  <span className="spinner w-4 h-4" /> Analisi in corso...
                </span>
              ) : (
                "Analizza File"
              )}
            </Button>
          </div>
        </Card>
      )}

      {/* Step 2: Preview */}
      {step === 2 && (
        <div className="space-y-4">
          {/* Stats bar */}
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Badge variant="info">
                  {equipment.length}{" "}
                  {mode === "update"
                    ? "verifiche estratte"
                    : "apparecchiature estratte"}
                </Badge>
              </div>
              {parseStats?.duplicatesRemoved > 0 && (
                <Badge variant="warning">
                  {parseStats.duplicatesRemoved} duplicati rimossi
                </Badge>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>File: {parseStats?.filename}</span>
                <span>|</span>
                <span>RSA: {rsaName}</span>
              </div>
              <div className="ml-auto text-sm text-gray-600">
                {selectedRows.size} / {equipment.length} selezionate
              </div>
            </div>
          </Card>

          {/* Warnings */}
          {parseWarnings.length > 0 && (
            <Card className="p-4 bg-yellow-50 border-yellow-200">
              <h3 className="font-medium text-yellow-800 mb-2">Avvisi</h3>
              <ul className="text-sm text-yellow-700 space-y-1">
                {parseWarnings.map((w, i) => (
                  <li key={i}>{w.warning}</li>
                ))}
              </ul>
            </Card>
          )}

          {/* Errors */}
          {parseErrors.length > 0 && (
            <Card className="p-4 bg-red-50 border-red-200">
              <h3 className="font-medium text-red-800 mb-2">Errori</h3>
              <ul className="text-sm text-red-700 space-y-1">
                {parseErrors.map((e, i) => (
                  <li key={i}>{e.error}</li>
                ))}
              </ul>
            </Card>
          )}

          {/* Equipment table */}
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={
                          selectedRows.size === equipment.length &&
                          equipment.length > 0
                        }
                        onChange={toggleAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      #
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Inventario
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Tipo Apparecchiatura
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Marca
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Modello
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Seriale
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Data Verifica
                    </th>
                    {mode === "update" && (
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Prossima Manut.
                      </th>
                    )}
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Fonte
                    </th>
                    <th className="px-3 py-3" />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {equipment.map((eq, i) => (
                    <tr
                      key={i}
                      className={`${!selectedRows.has(i) ? "opacity-40" : ""} hover:bg-gray-50`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedRows.has(i)}
                          onChange={() => toggleRow(i)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-400">
                        {i + 1}
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={eq.matricolaInterna || ""}
                          onChange={(e) =>
                            updateField(i, "matricolaInterna", e.target.value)
                          }
                          className="w-20 text-xs border-gray-200 rounded px-1 py-0.5"
                          placeholder="-"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={eq.tipoApparecchiatura || ""}
                          onChange={(e) =>
                            updateField(
                              i,
                              "tipoApparecchiatura",
                              e.target.value,
                            )
                          }
                          className="w-40 text-xs border-gray-200 rounded px-1 py-0.5"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={eq.marca || ""}
                          onChange={(e) =>
                            updateField(i, "marca", e.target.value)
                          }
                          className="w-28 text-xs border-gray-200 rounded px-1 py-0.5"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={eq.modello || ""}
                          onChange={(e) =>
                            updateField(i, "modello", e.target.value)
                          }
                          className="w-28 text-xs border-gray-200 rounded px-1 py-0.5"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={eq.codiceSeriale || ""}
                          onChange={(e) =>
                            updateField(i, "codiceSeriale", e.target.value)
                          }
                          className="w-28 text-xs border-gray-200 rounded px-1 py-0.5 font-mono"
                        />
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {mode === "update" ? (
                          <input
                            type="text"
                            value={eq.dataUltimaVerifica || ""}
                            onChange={(e) =>
                              updateField(
                                i,
                                "dataUltimaVerifica",
                                e.target.value,
                              )
                            }
                            className="w-24 text-xs border-gray-200 rounded px-1 py-0.5"
                            placeholder="DD/MM/YYYY"
                          />
                        ) : (
                          eq.dataUltimaVerifica || "-"
                        )}
                      </td>
                      {mode === "update" && (
                        <td className="px-3 py-2 text-xs text-green-600 font-medium">
                          {eq.dataUltimaVerifica?.match(/^\d{2}\/\d{2}\/\d{4}$/)
                            ? (() => {
                                const [d, m, y] =
                                  eq.dataUltimaVerifica.split("/");
                                const next = new Date(`${y}-${m}-${d}`);
                                next.setFullYear(next.getFullYear() + 2);
                                return next.toLocaleDateString("it-IT");
                              })()
                            : "-"}
                        </td>
                      )}
                      <td className="px-3 py-2 text-xs text-gray-400">
                        {eq.source || "-"}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => removeRow(i)}
                          className="text-gray-400 hover:text-red-500"
                          title="Rimuovi"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex justify-between">
            <Button
              variant="secondary"
              onClick={() => {
                setStep(1);
                setEquipment([]);
                setParseErrors([]);
                setParseWarnings([]);
              }}
            >
              Indietro
            </Button>
            <Button onClick={handleImport} disabled={selectedRows.size === 0}>
              {mode === "update"
                ? `Aggiorna ${selectedRows.size} verifiche in ${rsaName}`
                : `Importa ${selectedRows.size} apparecchiature in ${rsaName}`}
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Import results */}
      {step === 3 && (
        <Card className="p-6 space-y-6">
          {/* Progress bar */}
          {importing && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {mode === "update"
                    ? `Aggiornamento in corso verso ${rsaName}...`
                    : `Import in corso verso ${rsaName}...`}
                </span>
                <span className="font-medium">{importProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-primary-500 h-3 rounded-full transition-all"
                  style={{ width: `${importProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Results */}
          {importResults && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {mode === "update"
                  ? "Risultato Aggiornamento"
                  : "Risultato Import"}
              </h3>

              {mode === "update" ? (
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="p-4 text-center bg-green-50">
                      <p className="text-3xl font-bold text-green-700">
                        {importResults.updated || 0}
                      </p>
                      <p className="text-sm text-green-600">Aggiornate</p>
                    </Card>
                    <Card className="p-4 text-center bg-yellow-50">
                      <p className="text-3xl font-bold text-yellow-700">
                        {importResults.unmatched || 0}
                      </p>
                      <p className="text-sm text-yellow-600">Non trovate</p>
                    </Card>
                    <Card className="p-4 text-center bg-red-50">
                      <p className="text-3xl font-bold text-red-700">
                        {importResults.errors?.length || 0}
                      </p>
                      <p className="text-sm text-red-600">Errori</p>
                    </Card>
                  </div>

                  {/* Updated details */}
                  {importResults.details?.updated?.length > 0 && (
                    <Card className="p-4 bg-green-50 border-green-200">
                      <h4 className="font-medium text-green-800 mb-2">
                        Apparecchiature aggiornate (
                        {importResults.details.updated.length})
                      </h4>
                      <ul className="text-sm text-green-700 space-y-1 max-h-40 overflow-y-auto">
                        {importResults.details.updated.map((u, i) => (
                          <li key={i}>
                            <span className="font-medium">{u.nome}</span>
                            {" — "}
                            Verifica: {u.verDate}, Prossima: {u.nextDate}
                            <span className="text-green-500 ml-1">
                              (match: {u.matchedBy})
                            </span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}

                  {/* Unmatched details */}
                  {importResults.details?.unmatched?.length > 0 && (
                    <Card className="p-4 bg-yellow-50 border-yellow-200">
                      <h4 className="font-medium text-yellow-800 mb-2">
                        Non trovate ({importResults.details.unmatched.length})
                      </h4>
                      <ul className="text-sm text-yellow-700 space-y-1 max-h-40 overflow-y-auto">
                        {importResults.details.unmatched.map((u, i) => (
                          <li key={i}>
                            {u.codiceSeriale && (
                              <span>S/N: {u.codiceSeriale} </span>
                            )}
                            {u.marca && <span>({u.marca}) </span>}
                            {u.matricolaInterna && (
                              <span>Mat: {u.matricolaInterna} </span>
                            )}
                            {" — "}
                            <span className="text-yellow-600">{u.reason}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}
                </>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <Card className="p-4 text-center bg-green-50">
                    <p className="text-3xl font-bold text-green-700">
                      {importResults.created}
                    </p>
                    <p className="text-sm text-green-600">Create</p>
                  </Card>
                  <Card className="p-4 text-center bg-yellow-50">
                    <p className="text-3xl font-bold text-yellow-700">
                      {importResults.skipped}
                    </p>
                    <p className="text-sm text-yellow-600">
                      Saltate (gia esistenti)
                    </p>
                  </Card>
                  <Card className="p-4 text-center bg-red-50">
                    <p className="text-3xl font-bold text-red-700">
                      {importResults.errors?.length || 0}
                    </p>
                    <p className="text-sm text-red-600">Errori</p>
                  </Card>
                </div>
              )}

              {/* Error details */}
              {importResults.errors?.length > 0 && (
                <Card className="p-4 bg-red-50 border-red-200">
                  <h4 className="font-medium text-red-800 mb-2">
                    Dettaglio errori
                  </h4>
                  <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                    {importResults.errors.map((e, i) => (
                      <li key={i}>
                        {e.equipment && (
                          <span className="font-medium">{e.equipment}: </span>
                        )}
                        {e.error}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button onClick={() => navigate("/apparecchiature")}>
                  Vai alla lista apparecchiature
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setStep(1);
                    setFile(null);
                    setEquipment([]);
                    setParseErrors([]);
                    setParseWarnings([]);
                    setImportResults(null);
                    setImportProgress(0);
                  }}
                >
                  {mode === "update"
                    ? "Aggiorna altro file"
                    : "Importa altro file"}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

export default ImportApparecchiaturePage;
